<template>
  <div class="modal fade createNewModal" id="AffectationCaisseModal" tabindex="-1"
    aria-labelledby="AffectationCaisseModalLabel" aria-hidden="true" ref="modalRef">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content p-15 p-md-40">

        <div class="modal-header d-flex justify-content-between align-items-center ps-0 pe-0 pt-0 pb-15 pb-md-25">
          <h5 class="modal-title fw-bold text-black">
            {{ selectedAffectation ? 'Réaffecter' : 'Affecter' }} une caisse
          </h5>
          <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"
            @click="resetForm"></button>
        </div>

        <div class="modal-body ps-0 pe-0 pb-0 pt-15 pt-md-25">
          <Form ref="affectationForm" :validation-schema="affectationSchema" @submit="handleSubmit">
            <div class="row">

              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Utilisateur <span class="text-danger">*</span>
                  </label>
                  <Field name="userId" v-slot="{ field, errors }">
                    <Multiselect v-model="selectedUserId" :options="userOptions" :preserve-search="true"
                      :multiple="false" :searchable="true" placeholder="Sélectionner l'utilisateur" label="label"
                      track-by="value" :class="{ 'is-invalid': errors.length }"
                      @update:modelValue="field.onChange($event)" />
                    <ErrorMessage name="userId" class="text-danger" />
                  </Field>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Caisse <span class="text-danger">*</span>
                  </label>

                  <div v-if="selectedCaisse">
                    <input type="text" class="form-control" :value="selectedCaisse.nom" disabled />
                    <Field name="caisseId" type="hidden" :value="selectedCaisse.id" />
                  </div>

                  <Field v-else name="caisseId" v-slot="{ field, errors }">
                    <Multiselect v-model="selectedCaisseId" :options="caissesOptions" :preserve-search="true"
                      :multiple="false" :searchable="true" placeholder="Sélectionner une caisse" label="label"
                      track-by="value" :class="{ 'is-invalid': errors.length }"
                      @update:modelValue="field.onChange($event)" />
                    <ErrorMessage name="caisseId" class="text-danger" />
                  </Field>

                </div>
              </div>

              <div class="col-md-6 mb-3form-group ">
                <label class="d-block text-black fw-semibold mb-10">
                  Date d'affectation
                </label>
                <Field name="dateAffectation" type="date" :value="getCurrentDate()"
                  class="form-control shadow-none fs-md-15 text-black" disabled />
                <ErrorMessage name="dateAffectation" class="text-danger" />
              </div>


              <!-- Statut grisé -->
              <div class="col-md-6 mb-3">
                <label class="d-block text-black fw-semibold">Statut</label>
                <input type="text" class="form-control" value="Actif" disabled />
              </div>

            </div>

            <div class="d-flex justify-content-end gap-2 mt-20 mt-md-25 mt-3">
              <button type="button" class="btn btn-danger" data-bs-dismiss="modal" @click="resetForm">
                Annuler
              </button>
              <button class="btn btn-primary submit">{{ selectedAffectation ? 'Réaffecter' : 'Affecter' }}</button>

            </div>

          </Form>
        </div>

      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import ApiService from "@/services/ApiService";
import { error, success, hideModal } from "@/utils/utils";
import { User } from "@/models/users";
import { Caisse } from "@/models/Caisse";
import Multiselect from "@vueform/multiselect";

interface FormValues {
  userId: number | null;
  caisseId: number | null;
  dateAffectation: string;
}

export default defineComponent({
  name: "AffectationCaisseModal",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  props: {
    selectedUser: {
      type: Object as () => User | null,
      default: null,
    },
    selectedCaisse: { type: Object as () => Caisse | null, default: null },
    selectedAffectation: { type: Object as () => import("@/models/Affectationn").Affectationn | null, default: null },

  },
  emits: ["affectee", "openmodal"],
  setup(props, { emit }) {
    const modalRef = ref<HTMLElement | null>(null);
    const affectationForm = ref<InstanceType<typeof Form> | null>(null);
    const users = ref<User[]>([]);
    const caisses = ref<Caisse[]>([]);
    // const selectedUserId = ref<number | null>(null);
    // const selectedCaisseId = ref<number | null>(null);
    const selectedUserId = ref<number | string | null>(null);
    const selectedCaisseId = ref<number | string | null>(null);



    // Schéma de validation
    const affectationSchema = Yup.object().shape({
      userId: Yup.number()
        .required("L'utilisateur est obligatoire")
        .typeError("Veuillez sélectionner un utilisateur"),
      caisseId: Yup.number()
        .required("La caisse est obligatoire")
        .typeError("Veuillez sélectionner une caisse"),
      dateAffectation: Yup.string().required("La date est obligatoire"),

    });

    const userOptions = computed(() => {
      return users.value.map((user) => ({
        value: user.id,
        label: `${user.nom} ${user.prenom}`,
      }));
    });

    const caissesOptions = computed(() => {
      return caisses.value.map((caisse) => ({
        value: caisse.id,
        label: `${caisse.nom}`,
      }));
    });

    const getCurrentDate = () => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();
      return `${year}-${month}-${day}`;
    };


    const getAllUsers = async () => {
      try {
        const { data } = await ApiService.get("/users");
        users.value = data.data.data || [];
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la récupération des utilisateurs");
      }
    };

    const fetchAllCaisse = async () => {
      try {
        const { data } = await ApiService.get("/all/caisse?page=1&limit=1000");
        caisses.value = data.data.data || [];
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la récupération des caisses");
      }
    };

    watch(
      () => props.selectedCaisse,
      (newCaisse) => {
        if (newCaisse) {
          selectedCaisseId.value = newCaisse.id;
          affectationForm.value?.setFieldValue("caisseId", newCaisse.id);
        }
      },
      { immediate: true }
    );
    const now = new Date().toISOString();

    const handleSubmit = async (values: FormValues) => {
      try {
        console.log("Valeurs soumises :", values);
        if (props.selectedAffectation) {
          await ApiService.put(`/affectationCaisse/${props.selectedAffectation.id}`, {
            userId: values.userId,
            caisseId: values.caisseId,
            statut: "ACTIF",
            dateAffectation: now,
          });

          success("Caisse réaffectée avec succès");
        } else {
          await ApiService.post("/affectationCaisse", {
            userId: values.userId,
            caisseId: values.caisseId,
            dateAffectation: now,
            statut: "ACTIF"
          });

          success("Caisse affectée avec succès");
        }
        emit("affectee");
        resetForm();
        hideModal(modalRef.value);
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de l'affectation de la caisse");
      }
    };

    const resetForm = () => {
      selectedUserId.value = null;
      selectedCaisseId.value = null;
      affectationForm.value?.resetForm();
    };

    watch(
      () => props.selectedUser,
      (newUser) => {
        if (newUser) {
          selectedUserId.value = newUser.id;
          affectationForm.value?.setFieldValue("userId", newUser.id);
          emit("openmodal", modalRef.value);
        }
      },
      { immediate: true }
    );

    watch(selectedUserId, (newValue) => {
      affectationForm.value?.setFieldValue("userId", newValue);
    });

    watch(selectedCaisseId, (newValue) => {
      affectationForm.value?.setFieldValue("caisseId", newValue);
    });


    watch(
      () => props.selectedAffectation,
      (newAffectation) => {
        if (newAffectation) {
          selectedUserId.value = newAffectation.user?.id || null;
          selectedCaisseId.value = newAffectation.caisse?.id || null;
          affectationForm.value?.setFieldValue("userId", newAffectation.user?.id || null);
          affectationForm.value?.setFieldValue("caisseId", newAffectation.caisse?.id || null);
        }
      },
      { immediate: true }
    );
    onMounted(async () => {
      await Promise.all([getAllUsers(), fetchAllCaisse()]);
    });

    return {
      modalRef,
      affectationForm,
      users,
      caisses,
      userOptions,
      caissesOptions,
      selectedUserId,
      selectedCaisseId,
      affectationSchema,
      handleSubmit,
      resetForm,
      getCurrentDate,
    };
  },
});
</script>